import { GALLERY_CONFIG } from '../gallery-config.js';
import { ArrayUtils } from '../utils/array-utils.js';
import { matchesOrientation } from '../services/product/product-utils.js';
import { getDefaultVariant as resolveDefaultVariant } from '../services/api/product-api.js';

/**
 * Artwork selection algorithms and helpers.
 */
export const GalleryBuilderSelection = {
  getDefaultVariant(variants) {
    const priority = GALLERY_CONFIG.DISPLAY.DEFAULT_VARIANT_PRIORITY;
    const store = this.$store.galleryCore;

    if (!store.variant_manager) {
      store.initVariantManager();
    }

    return resolveDefaultVariant(variants, store.variant_manager, priority);
  },

  initializeDefaultSelection() {
    if (
      this.$store.galleryCore.orientation_states.horizontal.selected_artworks.length === 0 &&
      this.$store.galleryCore.orientation_states.vertical.selected_artworks.length === 0
    ) {
      this.selectRandomDefaultsForBothOrientations();
    } else if (
      this.orientation === 'horizontal' &&
      this.$store.galleryCore.orientation_states.horizontal.selected_artworks.length === 0
    ) {
      this.selectRandomDefaultsForOrientation('horizontal');
    } else if (
      this.orientation === 'vertical' &&
      this.$store.galleryCore.orientation_states.vertical.selected_artworks.length === 0
    ) {
      this.selectRandomDefaultsForOrientation('vertical');
    }

    this.$store.galleryCore.layout_count = this.$store.galleryCore.current_orientation_layout;

    if (this.$store.galleryCore.selected_artworks.length === 0) {
      this.loadAlgorithmicSelection();
    } else if (this.$store.galleryCore.selected_artworks.length !== this.$store.galleryCore.layout_count) {
      const required = this.$store.galleryCore.layout_count;
      if (this.$store.galleryCore.selected_artworks.length > required) {
        this.$store.galleryCore.selected_artworks = this.$store.galleryCore.selected_artworks.slice(0, required);
      }
    }
  },

  selectRandomDefaultsForBothOrientations() {
    const horizontal_products = this.all_products.filter((product) =>
      matchesOrientation(product, 'horizontal')
    );
    const vertical_products = this.all_products.filter((product) =>
      matchesOrientation(product, 'vertical')
    );

    const min_index = GALLERY_CONFIG.LAYOUT.MIN_CAROUSEL_INDEX;

    if (horizontal_products.length > min_index + GALLERY_CONFIG.LAYOUT.TRIO_COUNT) {
      const h_candidates = horizontal_products.slice(min_index);
      const h_selection = ArrayUtils.randomizeSelection(
        h_candidates,
        Math.min(GALLERY_CONFIG.LAYOUT.TRIO_COUNT, h_candidates.length)
      );
      this.$store.galleryCore.orientation_states.horizontal.selected_artworks = h_selection;
    } else {
      this.$store.galleryCore.orientation_states.horizontal.selected_artworks = horizontal_products.slice(
        0,
        GALLERY_CONFIG.LAYOUT.TRIO_COUNT
      );
    }

    if (vertical_products.length > min_index + GALLERY_CONFIG.LAYOUT.TRIO_COUNT) {
      const v_candidates = vertical_products.slice(min_index);
      const v_selection = ArrayUtils.randomizeSelection(
        v_candidates,
        Math.min(GALLERY_CONFIG.LAYOUT.TRIO_COUNT, v_candidates.length)
      );
      this.$store.galleryCore.orientation_states.vertical.selected_artworks = v_selection;
    } else {
      this.$store.galleryCore.orientation_states.vertical.selected_artworks = vertical_products.slice(
        0,
        GALLERY_CONFIG.LAYOUT.TRIO_COUNT
      );
    }

    this.$store.galleryCore.orientation_states.horizontal.layout = GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT;
    this.$store.galleryCore.orientation_states.vertical.layout = GALLERY_CONFIG.LAYOUT.DEFAULT_LAYOUT_COUNT;
  },

  selectRandomDefaultsForOrientation(orientation) {
    const orientation_products = this.all_products.filter((product) =>
      matchesOrientation(product, orientation)
    );

    const min_index = GALLERY_CONFIG.LAYOUT.MIN_CAROUSEL_INDEX;

    if (orientation_products.length > min_index + GALLERY_CONFIG.LAYOUT.TRIO_COUNT) {
      const candidates = orientation_products.slice(min_index);
      const selection = ArrayUtils.randomizeSelection(
        candidates,
        Math.min(GALLERY_CONFIG.LAYOUT.TRIO_COUNT, candidates.length)
      );

      if (orientation === 'horizontal') {
        this.$store.galleryCore.orientation_states.horizontal.selected_artworks = selection;
      } else {
        this.$store.galleryCore.orientation_states.vertical.selected_artworks = selection;
      }
    } else {
      const selection = orientation_products.slice(0, GALLERY_CONFIG.LAYOUT.TRIO_COUNT);
      if (orientation === 'horizontal') {
        this.$store.galleryCore.orientation_states.horizontal.selected_artworks = selection;
      } else {
        this.$store.galleryCore.orientation_states.vertical.selected_artworks = selection;
      }
    }
  },

  loadAlgorithmicSelection() {
    const available = this.filtered_artworks;
    const selection_count = GALLERY_CONFIG.LAYOUT.TRIO_COUNT;

    if (available.length >= selection_count) {
      const diverse_selection = this.selectDiverseArtworks(available, selection_count);
      const current_state = this.$store.galleryCore.orientation_states[this.orientation];
      current_state.selected_artworks = diverse_selection;

      this.$store.galleryCore.selectArtworks(
        diverse_selection.slice(0, this.required_artwork_count)
      );
    }
  },

  selectDiverseArtworks(artworks, count = null) {
    const selection_count = count || this.required_artwork_count;
    const artists_used = new Set();
    const diverse_artworks = [];

    const min_index = GALLERY_CONFIG.LAYOUT.MIN_CAROUSEL_INDEX;
    const candidates = artworks.slice(min_index);

    for (const artwork of candidates) {
      if (!artists_used.has(artwork.artist) && diverse_artworks.length < selection_count) {
        diverse_artworks.push(artwork);
        artists_used.add(artwork.artist);
      }
    }

    if (diverse_artworks.length < selection_count) {
      const remaining = candidates.filter(
        (artwork) => !diverse_artworks.find((selected) => selected.id === artwork.id)
      );
      diverse_artworks.push(...remaining.slice(0, selection_count - diverse_artworks.length));
    }

    return diverse_artworks;
  }
};
